import {cdnImage, cdnUrl} from '@/util/cdnImage';
import type {ExpertisePanel} from '@/components/home-plain/HomeExpertiseShowcase';
const microsoft = cdnImage('partnership/Microsoft_logo.webp');
const hyperledger = cdnImage('partnership/hyperledger-logo.webp');
const johnson = cdnImage('partnership/johnson-controls.webp');
const ethereum = cdnImage('partnership/ethereum-logo.webp');
const AWSPartner = cdnImage('partnership/awspartner.webp');
export const partnershipData = [
    {
        src: microsoft,
        heading: 'Microsoft Cloud Solution Provider'
    },
    {
        src: hyperledger,
        heading: 'Hyperledger Fabric'
    },
    {
        src: johnson,
        heading: 'Johnson Controls'
    },
    {
        src: ethereum,
        heading: 'Ethereum'
    },
    {
        src: AWSPartner,
        heading: 'AWS Partner Network'
    }
];

// Tabbed industry section (home page). Images reuse the existing industry artwork;
// every `redirect` points at a live route on the site.
export const homeIndustryTabsData = [
    {
        title: 'Healthcare',
        desc: 'We engineer connected healthcare systems around clinical workflows, patient experiences, interoperability, and secure health data.',
        image: cdnUrl('home-page-updated-assets/heathcare-img.webp'),
        imageAlt: 'Doctor reviewing patient vitals on a tablet beside a hospital monitoring display',
        link: '/healthcare-app-development-company',
        services: [
            {heading: 'Healthcare App Development', redirect: '/healthcare-app-development-company'},
            {heading: 'Healthcare Software Development'},
            {heading: 'Telehealth App Development'},
            {heading: 'Healthcare AI Development'}
        ]
    },
    {
        title: 'Fintech',
        desc: 'We build financial platforms designed around transaction security, regulatory controls, data integrity, and high-volume operations.',
        image: cdnUrl('home-page-updated-assets/fintech-img.webp'),
        imageAlt: 'Person making a secure card payment on a laptop with digital banking icons overlaid',
        link: '/fintech-software-development',
        services: [
            {heading: 'Fintech App Development'},
            {heading: 'Fintech Software Development', redirect: '/fintech-software-development'},
            {heading: 'Fintech AI Development'},
            {heading: 'Digital Banking App Development'}
        ]
    },
    {
        title: 'Legal',
        desc: 'Purpose-built legal technology simplifies complex workflows, improves document management, strengthens client service, and supports efficient operations.',
        image: cdnUrl('home-page-updated-assets/legal-img.webp'),
        imageAlt: 'Lawyer reviewing case documents on a laptop beside legal reference books',
        link: '/custom-software-development-company',
        services: [
            {heading: 'Legal Software Development Company'},
            {heading: 'Legal App Development Company'},
            {heading: 'Legal Chatbot Development'},
            {heading: 'Legal Billing Software Development'}
        ]
    },
    {
        title: 'Real Estate',
        desc: 'We digitize property discovery, transactions, leasing, asset operations, and customer engagement across real estate businesses.',
        image: cdnUrl('home-page-updated-assets/real-estate-img.webp'),
        imageAlt: 'Property agent browsing listings on a tablet outside a modern hillside villa',
        link: '/real-estate-software-development',
        services: [
            {heading: 'Real Estate Software Development', redirect: '/real-estate-software-development'},
            {heading: 'Real Estate App Development'},
            {heading: 'Real Estate AI Solutions'},
            {heading: 'Real Estate Tokenization Development Company', redirect: '/real-estate-tokenization'}
        ]
    },
    {
        title: 'Logistics & Supply Chain',
        desc: 'We build connected platforms that improve visibility and coordination across transportation, warehousing, inventory, and fulfillment.',
        image: cdnUrl('home-page-updated-assets/logistics-supply-chain-img.webp'),
        imageAlt: 'Container ship, cargo truck and aircraft at a port representing multimodal logistics',
        link: '/transport-logistics-software-development',
        services: [
            {heading: 'Logistics Software Development', redirect: '/transport-logistics-software-development'},
            {heading: 'Logistics App Development'},
            {heading: 'Freight Management Software'},
            {heading: 'AI Solutions for Logistics'}
        ]
    },
    {
        title: 'EdTech',
        desc: 'We develop scalable learning ecosystems that simplify academic operations, support digital collaboration, and help institutions deliver scalable learning programs.',
        image: cdnUrl('home-page-updated-assets/edtech-img.webp'),
        imageAlt: 'Educator teaching an online class from a laptop with students joining by video',
        link: '/education-app-development-company',
        services: [
            {heading: 'Education App Development', redirect: '/education-app-development-company'},
            {heading: 'E-learning Platform Development'},
            {heading: 'EdTech Software Development Company'},
            {heading: 'Education AI Solution Development'}
        ]
    },
    {
        title: 'Travel & Hospitality',
        desc: 'Digital experiences shape how travelers discover, book, and manage their journeys. We help travel businesses simplify reservations and improve guest-facing services.',
        image: cdnUrl('home-page-updated-assets/travel-hospitality-img.webp'),
        imageAlt: 'Traveller booking a trip on a smartphone at an airport departure lounge',
        link: '/mobile-application-development-services',
        services: [
            {heading: 'Travel Software Development'},
            {heading: 'Travel App Development'},
            {heading: 'Hotel Booking App Development'}
        ]
    },
    {
        title: 'Supply Chain',
        desc: 'Across supply chains, connected systems improve inventory visibility, streamline workflows, reduce friction, and support smarter planning.',
        image: cdnUrl('home-page-updated-assets/supply-chain-img.webp'),
        imageAlt: 'Warehouse operator scanning inventory pallets with a handheld device',
        link: '/enterprise-blockchain-development',
        services: [
            {heading: 'Supply Chain Software Development'},
            {heading: 'Inventory Management Software'},
            {heading: 'Supply Chain AI Solutions'}
        ]
    },
    {
        title: 'Media & Entertainment',
        desc: 'Engage audiences through scalable media technology that manages content, personalizes experiences, and delivers entertainment across digital platforms.',
        image: cdnUrl('home-page-updated-assets/media-entertainment-img.webp'),
        imageAlt: 'Video editor colour grading footage on a dual-monitor studio workstation',
        link: '/media-entertainment-software-development',
        services: [
            {heading: 'Entertainment App Development'},
            {heading: 'Media & Entertainment Software Development', redirect: '/media-entertainment-software-development'},
            {heading: 'OTT Platform Development'},
            {heading: 'Media & Entertainment AI Solutions'}
        ]
    },
    {
        title: 'Manufacturing',
        desc: 'Smart manufacturing starts with connected software that links operations, equipment, and data for better visibility, automation, and decisions.',
        image: cdnUrl('home-page-updated-assets/manufacturing-img.webp'),
        imageAlt: 'Engineer monitoring automated production line data on a factory floor terminal',
        link: '/enterprise-software-development',
        services: [
            {heading: 'Manufacturing Software Development'},
            {heading: 'Manufacturing App Development'},
            {heading: 'Manufacturing AI Solutions'},
            {heading: 'Industrial IoT Software Development'}
        ]
    },
    {
        title: 'Automotive',
        desc: 'As mobility becomes software-driven, automotive solutions connect vehicles, customers, operations, and data to improve efficiency and experiences.',
        image: cdnUrl('home-page-updated-assets/automotive-img.webp'),
        imageAlt: 'Connected vehicle dashboard displaying navigation and telematics data',
        link: '/custom-software-development-company',
        services: [
            {heading: 'Automotive Software Development'},
            {heading: 'Automotive App Development'},
            {heading: 'Automotive AI Solutions'},
            {heading: 'Connected Vehicle Software Development'}
        ]
    }
];

const ankitJain = cdnImage('home-page-new/Ankit-Jain-client.webp');
const jamesMc = cdnImage('home-page-new/James-McManus-client.webp');
const juanGira = cdnImage('home-page-new/Juan-Giraldez-client.webp');
const bilalHammoud = cdnImage('white-label-crypto-exchage/Bilal-Hammoud.webp');
const mikeRino = cdnImage('Blockchain-images/Mike-Rino.webp');
const alexanderBarrett = cdnImage('Blockchain-images/Alexander-Barrett.webp');
const koreconxFounder = cdnImage('Blockchain-images/Oscar-Jofre.webp');
// Order matters: split in half for a 2-column vertical slider — each column must start and end
// with different card types so the loop seam (last item -> first item) never puts two video
// cards back to back. Col 1 = video,text,video,text; col 2 = text,video,text.
export const clientFeedbackData = [
    // Column 1
    {
        isVideo: true,
        name: 'Ankit Jain',
        designation: 'CEO, CIMET Australia',
        src: ankitJain,
        linkedin: 'https://au.linkedin.com/in/ankit-jain-a62568116',
        videoLink:
            'https://www.youtube.com/watch?v=F35BREJ5lcg&embeds_referring_euri=http%3A%2F%2F182.75.105.186%3A3087%2F&source_ve_path=Mjg2NjY&feature=emb_logo',
        projectLink: '/case-study/cimet'
    },
    {
        isVideo: false,
        name: 'Mike Rino',
        designation: 'CTO, Integra Ledger',
        src: mikeRino,
        linkedin: '',
        review: 'Working with Debut Infotech was instrumental in developing Integra Ledger, our blockchain-based digital signature platform. Their dedication to innovation and attention to detail ensured that we could create a product that rivals industry giants in both quality and affordability. The security and reliability they built into our platform have set a new standard in the e-signature landscape.',
        projectLink: '/case-study/integraledger'
    },
    {
        isVideo: true,
        name: 'Juan Giraldez',
        designation: 'CEO, Target Job, USA',
        src: juanGira,
        linkedin: 'https://pa.linkedin.com/in/juangiraldez',
        videoLink:
            'https://www.youtube.com/watch?v=CDxW3B1cwCc&embeds_referring_euri=http%3A%2F%2F182.75.105.186%3A3087%2F&source_ve_path=Mjg2NjY&feature=emb_logo',
        projectLink: '/portfolio'
    },
    {
        isVideo: false,
        name: 'Oscar Jofre',
        designation: 'CEO, KoreConX',
        src: koreconxFounder,
        linkedin: 'https://www.linkedin.com/in/oscarjofre',
        review: 'Partnering with Debut Infotech has been a transformative experience for KoreConX. Their expertise in blockchain technology helped us create a secure and efficient ecosystem for managing private capital markets. The collaboration has enabled us to deliver unparalleled value to our clients, streamlining investor relations, compliance, and corporate governance on a global scale.'
    },
    // Column 2
    {
        isVideo: false,
        name: 'Bilal Hammoud',
        designation: 'Founder, NDAX',
        src: bilalHammoud,
        linkedin: '',
        review: "Working with Debut Infotech has been a game-changer for NDAX. Their unparalleled expertise in blockchain, DeFi, and crypto wallet development, allowed us to build a seamless, robust platform that enhances user experience and security. Their team's dedication, innovative approach, and attention to detail were evident throughout the project. They consistently exceeded our expectations, ensuring NDAX's position as a leading crypto trading platform.",
        projectLink: '/case-study/ndax-ecosystem'
    },
    {
        isVideo: true,
        name: 'James McManus',
        designation: 'Director, Unique Publishing, UK',
        src: jamesMc,
        linkedin: 'https://ie.linkedin.com/in/james-mcmanus-409b1511',
        videoLink:
            'https://www.youtube.com/watch?v=fao7g3vmAX8&embeds_referring_euri=http%3A%2F%2F182.75.105.186%3A3087%2F&source_ve_path=Mjg2NjY&feature=emb_logo',
        projectLink: '/case-study/unique-school'
    },
    {
        isVideo: false,
        name: 'Alexander Barrett',
        designation: 'Founder and CEO, iFinca',
        src: alexanderBarrett,
        linkedin: '',
        review: 'Debut Infotech played a pivotal role in bringing my vision for iFinca to life. Their blockchain expertise and innovative approach have significantly enhanced the transparency and traceability of the coffee supply chain. Thanks to them, coffee farmers now have access to a platform that not only connects them directly with buyers but also ensures they receive fair pricing.',
        projectLink: '/case-study/ifinca'
    }
];

const web3 = cdnImage('Home-page/latest-image/Web3-Development.webp');
const aiDevelopment = cdnImage('Home-page/latest-image/AI-Development.webp');
const mlDevelopment = cdnImage('Home-page/latest-image/ML-Development.webp');
const blockchainDevelopment = cdnImage('Home-page/latest-image/Blockchain-Development.webp');
const DevOpsDevelopment = cdnImage('Home-page/latest-image/DevOps-Software-Development.webp');
const blockchainConsulting = cdnImage('Home-page/latest-image/AI-Blockchain-Consulting.webp');
const mobileApp = cdnImage('Home-page/latest-image/Mobile-App-Development.webp');
const webDevelopment = cdnImage('Home-page/latest-image/Web-Development.webp');
const softwareTesting = cdnImage('Home-page/latest-image/Software-Testing.webp');
export const endDevelopmentHomeData = [
    {
        src: webDevelopment,
        redirect: '/web-development',
        heading: 'Web Development',
        body: 'Our cutting-edge web app development services are tailored to the unique needs of businesses. By building responsive, user-friendly, and feature-rich web applications, we enable brands to drive engagement, enhance their online presence, and achieve long-term digital success.'
    },
    {
        src: mobileApp,
        redirect: '/mobile-application-development-services',
        heading: 'Mobile App Development',
        body: 'We employ our cross-industry experience to design tailored enterprise mobility solutions that help brands achieve 360-degree digital transformation. Our mobile apps drive sustainable growth, foster innovation, and ensure businesses maintain a competitive edge through cutting-edge technology.'
    },
    {
        src: aiDevelopment,
        redirect: '/ai-development-company',
        heading: 'AI Development',
        body: 'Our AI development services transform business operations by integrating intelligent systems that automate complex tasks, enhance productivity, and drive innovation. We specialize in creating customized AI solutions that enable businesses to make data-driven decisions and stay ahead in competitive markets.'
    },
    {
        src: mlDevelopment,
        redirect: '/machine-learning-development-company',
        heading: 'ML Development',
        body: 'Our machine learning development services enable businesses to leverage predictive analytics and deep learning models for improved accuracy and performance. We develop scalable ML solutions that automate data processing, optimize workflows, and deliver actionable insights.'
    },
    {
        src: blockchainConsulting,
        redirect: '',
        heading: 'Software Consulting Services ',
        body: 'Our software consulting helps organizations align technology choices with business priorities. From platform assessments to roadmap definition, we enable informed decisions that support sustainable delivery and operational resilience.'
    },
    {
        src: web3,
        redirect: '/web3-development-company',
        heading: 'Web3 Development',
        body: 'We leverage Web3 technologies to create decentralized apps that drive greater transparency, efficiency, and automation across all business functions. Our solutions empower businesses to deliver elevated product experiences and stay competitive in an increasingly decentralized digital landscape.'
    },
    {
        src: blockchainDevelopment,
        redirect: '/blockchain-development-services',
        heading: 'Blockchain Development',
        body: 'Through our comprehensive blockchain development services, we position brands to achieve unprecedented growth, efficiency, and automation. Our solutions enable trust and transparency across all operations, driving innovation and securing competitive advantages in rapidly evolving markets.'
    },
    {
        src: DevOpsDevelopment,
        redirect: '/devops',
        heading: 'DevOps Software Development',
        body: 'We offer DevOps software development services to streamline software delivery through automation, continuous integration, and agile methodologies. Our DevOps solutions enhance collaboration, accelerate release cycles, and improve software quality, keeping your business agile and resilient.'
    },
    {
        src: softwareTesting,
        redirect: '/software-testing-services',
        heading: 'Software Testing',
        body: 'Our comprehensive software testing services ensure flawless functionality, seamless user experiences, and robust security. We elevate the final software quality to excellence, helping businesses drive positive outcomes and maintain high standards in increasingly competitive digital environments.'
    }
];

const featureprologo1 = cdnImage('banner-bottom/featureprologo1.webp');
const featureprologo2 = cdnImage('banner-bottom/fox-news-logo.webp');
const featureprologo3 = cdnImage('banner-bottom/featureprologo3.webp');
const featureprologo4 = cdnImage('banner-bottom/featureprologo4.webp');
const featureprologo5 = cdnImage('banner-bottom/nbc.webp');
const featureprologo6 = cdnImage('banner-bottom/Techinasia.webp');
export const bannerBottom = [
    {src: featureprologo1, alt: 'Abc News'},
    {src: featureprologo2, alt: 'fox News'},
    {src: featureprologo3, alt: 'Appindex'},
    {src: featureprologo4, alt: 'Bloomberg Business'},
    {src: featureprologo5, alt: 'NBC News'},
    {src: featureprologo6, alt: 'TECHINASIA'},
    {src: featureprologo1, alt: 'Abc News'},
    {src: featureprologo2, alt: 'fox News'},
    {src: featureprologo3, alt: 'Appindex'},
    {src: featureprologo4, alt: 'Bloomberg Business'},
    {src: featureprologo5, alt: 'NBC News'},
    {src: featureprologo6, alt: 'TECHINASIA'}
];

const suzukiimg = cdnImage('clients/suzuki.webp');
const aigimg = cdnImage('clients/aig.webp');
const disnepimg = cdnImage('clients/disney.webp');
const phillipsimg = cdnImage('clients/philips.webp');
const nflimg = cdnImage('clients/nfl.webp');
const kfcimg = cdnImage('clients/kfclogo.webp');
const docusignimg = cdnImage('clients/docusign_logo.webp');
const newhdfcimg = cdnImage('clients/hdfc-logo-new.webp');
const bluescopeimg = cdnImage('clients/Bluescope-logo.webp');
const newifincaimg = cdnImage('clients/ifinca-logo.webp');
const econneximg = cdnImage('clients/econnex-logo.webp');
const everledgerimg = cdnImage('clients/leaftyme.webp');
const saviimg = cdnImage('clients/saavi.webp');
const getlicenseimg = cdnImage('clients/license.webp');
const uniqueimg = cdnImage('clients/unique-school-logo.webp');
export const clients = [
    {src: suzukiimg, alt: 'suzukiimg'},
    {src: aigimg, alt: 'aigimg'},
    {src: disnepimg, alt: 'disnepimg'},
    {src: phillipsimg, alt: 'philipsimg'},
    {src: nflimg, alt: 'nflimg'},
    {src: kfcimg, alt: 'kfcimg'},
    {src: docusignimg, alt: 'docusignimg'},
    {src: newhdfcimg, alt: 'hdfcimg'},
    {src: bluescopeimg, alt: 'bluescopeimg'},
    {src: newifincaimg, alt: 'ifincaimg'},
    {src: econneximg, alt: 'econneximg'},
    {src: everledgerimg, alt: 'everledgerimg'},
    {src: saviimg, alt: 'saaviimg'},
    {src: getlicenseimg, alt: 'getlicenseimg'},
    {src: uniqueimg, alt: 'uniqueimg'}
    // {src: integralogo, alt: 'integra'}
];

export const trustedByBrands = [
    {src: featureprologo1, alt: 'Abc News logo', label: 'Abc News'},
    {src: featureprologo2, alt: 'Fox News logo', label: 'Fox News'},
    {src: featureprologo3, alt: 'Appindex logo', label: 'Appindex'},
    {src: featureprologo4, alt: 'Bloomberg Business logo', label: 'Bloomberg Business'},
    {src: featureprologo5, alt: 'NBC News logo', label: 'NBC News'},
    {src: featureprologo6, alt: 'TECHINASIA logo', label: 'TECHINASIA'}
];

export const ImpactStatsData = [
    {tag: 'DEL', value: '500+', heading: 'Digital Solutions Delivered Across AI, Blockchain & Software'},
    {tag: 'EXP', value: '15+', heading: 'Years of Product & Software Engineering Experience'},
    {tag: 'SPD', value: '10 – 12 Weeks', heading: 'Typical Timeline From Product Brief to MVP Launch'},
    {tag: 'TAL', value: '150+', heading: 'Engineers & Technology Specialists Across Core Disciplines'},
    {tag: 'CAP', value: '$62M+', heading: 'Capital Raised by Platforms Engineered for Our Clients'},
    {tag: 'OFF', value: '4', heading: 'Global Offices Across the US, UK, Canada & India'},
    {tag: 'AWD', value: '20+', heading: 'Awards & Recognitions Across Technology & Engineering'},
    {tag: 'IND', value: '25+', heading: 'Industries Served With Product Engineering & AI Solutions'},
    {tag: 'TECH', value: '250+', heading: 'Technologies & Frameworks Used in Real-World Projects'}
];

export {socialMediaData} from '@/util/contents/social-media-data';

const openAiLogo = cdnImage('home-page-new/OpenAI.webp');
const anthropicLogo = cdnImage('home-page-new/Anthropic.webp');
const metaLlamaLogo = cdnImage('home-page-new/Meta.webp');
const mistralLogo = cdnImage('home-page-new/Mistral.webp');
const langchainLogo = cdnImage('home-page-new/LangChain.webp');
const nvidiaLogo = cdnImage('home-page-new/NVIDIA.webp');
const geminiLogo = cdnImage('home-page-new/google-gemini.webp');
const huggingfaceLogo = cdnImage('home-page-new/Hugging-Face.webp');
const awsBedrockLogo = cdnImage('home-page-new/AWS-Bedrock.webp');
const azureLogo = cdnImage('home-page-new/Microsoft-Azure-AI.webp');
const claudeLogo = cdnImage('home-page-new/Claude.webp');
const autogenLogo = cdnImage('home-page-new/Autogen.webp');
const crewaiLogo = cdnImage('home-page-new/CrewAI.webp');
const cohereLogo = cdnImage('home-page-new/Cohere.webp');
export const AIModelIntegrationData = [
    {title: 'OpenAI', src: openAiLogo},
    {title: 'Anthropic', src: anthropicLogo},
    {title: 'Meta (Llama)', src: metaLlamaLogo},
    {title: 'Mistral', src: mistralLogo},
    {title: 'LangChain', src: langchainLogo},
    {title: 'NVIDIA', src: nvidiaLogo},
    {title: 'Google (Gemini)', src: geminiLogo},
    {title: 'Hugging Face', src: huggingfaceLogo},
    {title: 'AWS Bedrock', src: awsBedrockLogo},
    {title: 'Microsoft Azure AI', src: azureLogo},
    {title: 'Claude', src: claudeLogo},
    {title: 'Autogen', src: autogenLogo},
    {title: 'CrewAI', src: crewaiLogo},
    {title: 'Cohere', src: cohereLogo}
];

export const HomeServicesV2Data = [
    {
        iconKey: 'product',
        title: 'Product Engineering (Web & Mobile)',
        description:
            'As a product engineering company, we turn complex ideas into reliable digital products through structured discovery, user-focused design, and scalable architecture. Our digital product engineering approach supports the full lifecycle, from MVP validation to modernization, while keeping performance, security, usability, and business goals aligned.',
        subServices: [
            {heading: 'MVP Development', redirect: '/blog/a-complete-guide-for-mvp-development-helping-you-save-resources'},
            {heading: 'Web Application Development', redirect: '/web-development'},
            {heading: 'Software Development Consulting', redirect: '/software-development-consulting-services'},
            {heading: 'SaaS Development', redirect: '/saas-development-company'},
            {heading: 'Mobile App Development', redirect: '/mobile-application-development-services'},
            {heading: 'iOS App Development', redirect: '/ios-app-development-services'},
            {heading: 'Android App Development', redirect: '/android-app-development'},
            {heading: 'Custom Software Development', redirect: '/custom-software-development-company'},
            {heading: 'Custom CMS Development', redirect: '/custom-cms-development-services'},
            {heading: 'Flutter App Development', redirect: '/flutter-app-development'}
        ],
        ctaText: 'Discuss Your Product Idea',
        ctaUrl: '/contact-us'
    },
    {
        iconKey: 'ai',
        title: 'AI Engineering & Optimization Services',
        description:
            'Our AI engineering services help businesses move AI from experimentation to production. Our <a href="/hire-ai-developers" target="_blank">AI engineers</a> combine expertise across NLP, computer vision, generative AI, and LLM systems to manage feasibility, development, deployment, and optimization around real business processes and operational requirements.',
        subServices: [
            {heading: 'AI Consulting Services', redirect: '/ai-consulting-company'},
            {heading: 'AI Development', redirect: '/ai-development-company'},
            {heading: 'AI Copilot Development', redirect: '/ai-copilot-development'},
            {heading: 'AI Prompt Engineering', redirect: '/ai-prompt-engineering-company'},
            {heading: 'AI Chatbot Development', redirect: '/ai-chatbot-development-company'},
            {heading: 'ChatGPT Integration', redirect: '/chatgpt-integration-services'},
            {heading: 'AI Agent Development', redirect: '/ai-agent-development-company'},
            {heading: 'LLM Model Development', redirect: '/large-language-model-development'},
            {heading: 'AI as a Service', redirect: '/ai-as-a-service'},
            {heading: 'Generative AI Development', redirect: '/generative-ai-development-company'}
        ],
        ctaText: 'Explore AI Services ',
        ctaUrl: '/ai-development-company'
    },
    {
        iconKey: 'teams',
        title: 'Hire Dedicated Developers & Tech Teams',
        description:
            'Our dedicated development teams help businesses accelerate delivery with experienced engineers aligned to specific product needs. Whether you need to hire dedicated developers across AI, blockchain, mobile, or full-stack engineering, our flexible teams integrate quickly, adapt to changing requirements, and support sustained product development.',
        subServices: [
            {heading: 'Hire Remote Developers', redirect: '/hire-remote-developers'},
            {heading: 'Hire Development Team', redirect: '/hire-development-team'},
            {heading: 'Hire Blockchain Developers', redirect: '/hire-blockchain-developers'},
            {heading: 'Hire AI Developers', redirect: '/hire-ai-developers'},
            {heading: 'Hire ChatGPT Developers', redirect: '/chatgpt-application-developers'},
            {heading: 'Hire AI Agent Developers', redirect: '/hire-ai-agent-developers'},
            {heading: 'Hire iOS Developers', redirect: '/hire-ios-developers'},
            {heading: 'Hire Android Developers', redirect: '/hire-android-app-developers'},
            {heading: 'Hire SaaS Developers', redirect: '/hire-saas-developers'},
            {heading: 'Hire Web Developers', redirect: '/hire-web-developers'}
        ],
        ctaText: 'Build Your Dedicated Team',
        ctaUrl: '/hire-development-team'
    },
    {
        iconKey: 'blockchain',
        title: 'Blockchain & Tokenization Development',
        description:
            'Our Blockchain Development expertise covers blockchain applications, smart contracts, tokenization platforms, and digital asset ecosystems. We combine technical depth with real-world implementation experience to build secure, scalable, and compliant Web3 solutions that support transparent operations, asset innovation, and evolving business requirements.',
        subServices: [
            {heading: 'Blockchain Development', redirect: '/blockchain-development-services'},
            {heading: 'Web3 Development', redirect: '/web3-development-company'},
            {heading: 'DeFi Development', redirect: '/decentralized-finance-defi-development'},
            {heading: 'Smart Contract Audit', redirect: '/smart-contract-audit-services'},
            {heading: 'Smart Contract Development', redirect: '/smart-contract-development'},
            {heading: 'Crypto Wallet Development', redirect: '/crypto-wallet'},
            {heading: 'Cryptocurrency Exchange Development', redirect: '/cryptocurrency-exchange-development'},
            {heading: 'Real Estate Tokenization', redirect: '/real-estate-tokenization'},
            {heading: 'Real World Asset Tokenization', redirect: '/real-world-asset-tokenization'},
            {heading: 'P2P Crypto Exchange', redirect: '/p2p-crypto-exchange-development'}
        ],
        ctaText: 'Explore Web3 Services ',
        ctaUrl: '/web3-development-company'
    },
    {
        iconKey: 'devops',
        title: 'DevOps & Quality Engineering',
        description:
            'Our DevOps Engineering and QA teams strengthen software delivery through automated testing, reliable CI/CD pipelines, and scalable infrastructure. By integrating quality engineering early, we help reduce defects, improve release reliability, maintain performance and security, and scale software delivery without unnecessary operational risk or technical debt.',
        subServices: [
            {heading: 'Software Testing & QA', redirect: '/software-testing-services'},
            {heading: 'Manual Testing', redirect: '/manual-testing'},
            {heading: 'Automation Testing', redirect: '/automation-testing-services'},
            {heading: 'DevOps Consulting & Development', redirect: '/devops'}
        ],
        ctaText: 'Explore DevOps Services',
        ctaUrl: '/devops'
    }
];

export const HomeExpertiseShowcaseData: ExpertisePanel[] = [
    {
        title: 'Agentic AI',
        shortTitle: 'Agentic AI',
        pointers: ['Multi-Agent Systems', 'Autonomous Task Execution', 'Tool & API Integration', 'Human-in-the-Loop Controls'],
        src: cdnUrl('home-page-updated-assets/Agentic-AI 1.webp'),
        alt: 'Profile of an android head with a glowing circuit-board brain, representing autonomous AI agents'
    },
    {
        title: 'AI Copilots',
        shortTitle: 'Copilots',
        pointers: ['Context-Aware Assistance', 'Workflow Integration', 'Enterprise Knowledge Access', 'Role-Based Experiences'],
        src: cdnUrl('home-page-updated-assets/Ai-copilots 1.webp'),
        alt: 'AI copilot assisting a professional with contextual suggestions inside a work application'
    },
    {
        title: 'LLM Fine-Tuning',
        shortTitle: 'Fine-Tuning',
        pointers: ['Domain-Specific Adaptation', 'Proprietary Data Training', 'Model Optimization', 'Output Alignment'],
        src: cdnUrl('home-page-updated-assets/llm-fine-tuning 1.webp'),
        alt: 'Language model being tuned on domain-specific data, shown as layered neural network nodes'
    },
    {
        title: 'Generative AI',
        shortTitle: 'Generative AI',
        pointers: ['Text & Content Generation', 'Multimodal AI', 'Enterprise GenAI Applications', 'Model Integration'],
        src: cdnUrl('home-page-updated-assets/generative-ai.webp'),
        alt: 'Generative AI producing text, imagery and multimodal content from a single prompt'
    },
    {
        title: 'Data Engineering',
        shortTitle: 'Data',
        pointers: ['AI-Ready Data Pipelines', 'Data Integration', 'Processing & Transformation', 'Data Quality Management'],
        src: cdnUrl('home-page-updated-assets/data-engineering.webp'),
        alt: 'Data pipelines moving and transforming enterprise data into AI-ready datasets'
    },
    {
        title: 'AI Workflow Automation',
        shortTitle: 'Automation',
        pointers: [
            'Intelligent Process Automation',
            'Cross-System Orchestration',
            'AI-Driven Decision Flows',
            'Approval & Exception Handling'
        ],
        src: cdnUrl('home-page-updated-assets/ai-workflow-automation.webp'),
        alt: 'Automated workflow orchestrating decisions and approvals across connected business systems'
    }
];

export const HomeDeliveryGuaranteeData = {
    cards: [
        {
            title: '10–12 Week MVP Commitment',
            subheading:
                'We commit to the agreed milestone timeline upfront in writing. If we miss a delivery target without a documented scope change initiated by the client, we credit a free engineering sprint. Speed is a discipline, not a promise.',
            badge: 'Contractually Backed',
            iconName: 'bolt',
            featured: true
        },
        {
            title: 'Confidential Engagement',
            subheading:
                'A mutual NDA is signed before any technical discussion takes place, before you share your idea, your architecture, or your data. No exceptions. Your IP is protected from the first conversation, not from the contract signature.',
            badge: 'Zero-Exception Policy',
            iconName: 'lock'
        },
        {
            title: 'Your IP. Day One.',
            subheading:
                'All intellectual property developed during the engagement, including code, models, documentation, and data pipelines, is fully and legally owned by the client upon project completion. We do not retain any rights, licences, or access to the assets we build for you.',
            badge: 'Full Ownership Transfer',
            iconName: 'security'
        }
    ],
    ctaText: 'Start Your Project with Confidence',
    ctaUrl: '/contact-us'
};

export const homeFaqData = [
    {
        category: 'General',
        questions: [
            {
                q: 'What does Debut Infotech specialize in?',
                a: 'Debut Infotech specializes in engineering and evolving digital products where software, AI, data, blockchain, and cloud infrastructure work as one system. As a <strong>product engineering company</strong>, we support the complete product lifecycle, from strategy and architecture to development, QA, deployment, modernization, and scaling, with technical decisions shaped around reliability, security, integrations, performance, and long-term maintainability.'
            },
            {
                q: 'Can you provide dedicated product engineering teams?',
                a: 'Yes. Dedicated teams are most effective when they provide capability ownership, not simply additional headcount. Our <strong>product engineering services for SaaS companies</strong> and enterprises can combine software engineers, AI specialists, blockchain developers, QA, DevOps, and technical leadership according to the roadmap. Team composition can change as the product moves from architecture and build into integration, optimization, scaling, or continuous feature delivery.'
            },
            {
                q: 'How do I choose the right product engineering company?',
                a: 'Start with evidence of comparable delivery rather than team size or technology lists. A capable <strong>product engineering company for startups and enterprises</strong> should be able to explain architectural trade-offs, identify delivery risks early, show how quality is governed, and support the product beyond launch. Review case studies, engineering depth, security practices, IP terms, communication structure, and how the team handles changing requirements before comparing hourly rates alone.'
            },
            {
                q: 'Can you help build a product from scratch or develop an MVP?',
                a: 'Yes. Effective <strong>MVP development</strong> is about validating the right assumptions with the smallest credible product, not simply shipping fewer features. We first clarify the user problem, business objective, core workflows, technical constraints, and success criteria. Architecture is then kept proportionate to the current stage while avoiding shortcuts that would make the product unnecessarily expensive to extend once usage, integrations, or feature complexity increase.'
            },
            {
                q: 'Can you modernize and scale an existing software product?',
                a: 'Yes. Modernization should begin with diagnosis rather than an automatic rewrite. We assess architecture, code quality, infrastructure, dependencies, integrations, performance bottlenecks, and operational risk before deciding what should be retained, refactored, replaced, or migrated. Our <strong>enterprise software modernization and digital transformation</strong> work is structured to improve scalability and maintainability while protecting business continuity and avoiding unnecessary disruption to systems that still perform effectively.'
            },
            {
                q: 'Can Debut Infotech integrate AI into an existing software product?',
                a: 'Yes. AI integration usually works best when it begins with the workflow and data rather than the model. Through our <strong>software modernization and AI integration services</strong>, we assess where AI can materially improve an existing product, what data it requires, how outputs should be controlled, and where human oversight remains necessary. We can then integrate agents, copilots, RAG, document intelligence, recommendations, or automation without rebuilding the entire application.'
            },
            {
                q: 'What AI services does Debut Infotech offer?',
                a: 'Our <strong>AI-powered software product development services</strong> cover generative AI, agentic systems, copilots, RAG, LLM integration, fine-tuning, data engineering, document intelligence, and workflow automation. The technology selection comes after the use case. We determine whether the requirement needs retrieval, generation, prediction, orchestration, or autonomous execution, then design the data, integration, evaluation, security, and monitoring layers required to operate that capability reliably in production.'
            },
            {
                q: 'Can you integrate new software with existing enterprise systems?',
                a: 'Yes. Enterprise integration is primarily an architecture and data-consistency problem, not an API-connection exercise. We define the <strong>solution architecture</strong> around systems of record, data ownership, authentication, transaction flows, failure handling, latency requirements, and integration dependencies. New software can then connect with ERP, CRM, identity, cloud, database, legacy, and third-party environments without creating brittle point-to-point integrations that become difficult to maintain as the ecosystem grows.'
            },
            {
                q: 'Can you take over an existing product or codebase?',
                a: 'Yes. The first step is a technical assessment covering architecture, repositories, dependencies, environments, documentation, security exposure, test coverage, deployment pipelines, and known technical debt. Under a <strong>Product Lifecycle Engineering</strong> approach, we establish operational stability and understand the system before making major changes. This allows us to distinguish urgent remediation from longer-term modernization instead of treating an inherited codebase as something that automatically needs to be rewritten.'
            },
            {
                q: 'How do you maintain quality and communication throughout development?',
                a: 'Quality needs to be engineered into the delivery process rather than inspected at the end. We use defined acceptance criteria, peer reviews, automated and manual testing, architecture oversight, sprint reviews, issue tracking, and release controls throughout development. Communication follows the same principle: clients should have visibility into progress, decisions, risks, dependencies, and upcoming priorities so problems surface while they are still inexpensive to resolve.'
            },
            {
                q: 'How do you protect client data, intellectual property, and compliance requirements?',
                a: 'Security begins with architecture and access design, not a pre-launch checklist. We use confidentiality agreements, contractual IP ownership, least-privilege access, secure coding practices, code review, encryption, environment controls, and defined development processes. Where regulation applies, we design data flows, retention, auditability, permissions, and operational controls around relevant requirements such as GDPR, HIPAA, PCI DSS, or other applicable frameworks rather than treating compliance as documentation added after development.'
            }
        ]
    }
];
// ─── CompetitiveComparison ────────────────────────────────────────────────────

// ─── ComplianceStandards ──────────────────────────────────────────────────────

export const ComplianceStandardsData = {
    rows: [
        {
            iconType: 'shield',
            layer: 'Data Privacy and Residency',
            standards: [
                {
                    name: 'GDPR',
                    description: 'Privacy by design, consent, data rights, and processing controls.',
                    image: cdnUrl('home-page-updated-assets/gdpr-compliance.svg')
                },
                {
                    name: 'CCPA / CPRA',
                    description: 'Consumer rights, disclosure, access, and deletion controls.',
                    image: cdnUrl('home-page-updated-assets/ccpa-compliance.svg')
                },
                {
                    name: 'HIPAA',
                    description: 'Administrative and technical safeguards for protected data.',
                    image: cdnUrl('home-page-updated-assets/hipaa-compliance.svg')
                },
                {
                    name: 'UK GDPR',
                    description: 'UK data protection duties applied alongside EU obligations.',
                    image: cdnUrl('home-page-updated-assets/uk-gdpr-compliance.svg')
                },
                {
                    name: 'PIPEDA',
                    description: 'Canadian consent, retention, and disclosure requirements.',
                    image: cdnUrl('home-page-updated-assets/pipeda-compliance.svg')
                },
                {
                    name: 'ISO/IEC 27001',
                    description: 'A certified information security management system.',
                    image: cdnUrl('home-page-updated-assets/iso-27001-compliance.svg')
                }
            ]
        },
        {
            iconType: 'key',
            layer: 'Identity, Access, and Control',
            standards: [
                {
                    name: 'RBAC Principles',
                    description: 'Least-privilege roles enforced at every service boundary.',
                    image: cdnUrl('home-page-updated-assets/rbac-compliance.svg')
                },
                {
                    name: 'Zero Trust Architecture',
                    description: 'Every request authenticated and authorised, never assumed.',
                    image: cdnUrl('home-page-updated-assets/zero-trust-compliance.svg')
                },
                {
                    name: 'NIST 800-53',
                    description: 'Control families mapped to access and audit requirements.',
                    image: cdnUrl('home-page-updated-assets/nist-800-53-compliance.svg')
                },
                {
                    name: 'MFA',
                    description: 'Multi-factor verification enforced on privileged access.',
                    image: cdnUrl('home-page-updated-assets/mfa-compliance.svg')
                },
                {
                    name: 'ISO/IEC 27002',
                    description: 'Implementation guidance for access and operational controls.',
                    image: cdnUrl('home-page-updated-assets/iso-iec-27002-compliance.svg')
                }
            ]
        },
        {
            iconType: 'code',
            layer: 'Secure Development and Testing',
            standards: [
                {
                    name: 'OWASP Top 10',
                    description: 'Common web and API risks reviewed before every release.',
                    image: cdnUrl('home-page-updated-assets/owasp-top-10-compliance.svg')
                },
                {
                    name: 'SAST / DAST Practices',
                    description: 'Static and dynamic scanning wired into the delivery pipeline.',
                    image: cdnUrl('home-page-updated-assets/sast-dast-compliance.svg')
                },
                {
                    name: 'Secure SDLC',
                    description: 'Threat modelling and code review at each development stage.',
                    image: cdnUrl('home-page-updated-assets/secure-sdlc-compliance.svg')
                },
                {
                    name: 'OWASP ASVS',
                    description: 'Verification requirements applied level by level to each build.',
                    image: cdnUrl('home-page-updated-assets/owasp-asvs-compliance.svg')
                },
                {
                    name: 'NIST SSDF',
                    description: 'Secure software development practices across the delivery lifecycle.',
                    image: cdnUrl('home-page-updated-assets/nist-ssdf-compliance.svg')
                },
                {
                    name: 'SBOM Practices',
                    description: 'Dependency inventories maintained and checked for known risks.',
                    image: cdnUrl('home-page-updated-assets/sbom-practices-compliance.svg')
                }
            ]
        },
        {
            iconType: 'card',
            layer: 'Payments and Transaction Integrity',
            standards: [
                {
                    name: 'PCI DSS',
                    description: 'Cardholder data isolated, tokenised, and never logged.',
                    image: cdnUrl('home-page-updated-assets/pci-dss-compliance.svg')
                },
                {
                    name: 'Strong Customer Authentication',
                    description: 'Multi-factor verification aligned to PSD2 requirements.',
                    image: cdnUrl('home-page-updated-assets/sca-compliance.svg')
                },
                {
                    name: 'PSD2',
                    description: 'Open banking access, consent, and payment security rules.',
                    image: cdnUrl('home-page-updated-assets/psd2-compliance.svg')
                },
                {
                    name: 'EMV Security Standards',
                    description: 'Chip and contactless transaction integrity for card payments.',
                    image: cdnUrl('home-page-updated-assets/emv-security-standards-compliance.svg')
                }
            ]
        },
        {
            iconType: 'gear',
            layer: 'AI Governance and Model Oversight',
            standards: [
                {
                    name: 'EU AI Act',
                    description: 'Risk classification, documentation, and human oversight.',
                    image: cdnUrl('home-page-updated-assets/eu-ai-act-readiness-compliance.svg')
                },
                {
                    name: 'OECD AI Principles',
                    description: 'Transparency, accountability, and fairness in model use.',
                    image: cdnUrl('home-page-updated-assets/oecd-ai-principles-compliance.svg')
                },
                {
                    name: 'ISO/IEC 42001',
                    description: 'A managed lifecycle for AI systems and their controls.',
                    image: cdnUrl('home-page-updated-assets/iso-iec-42001-compliance.svg')
                },
                {
                    name: 'NIST AI RMF',
                    description: 'Risk mapped, measured, and managed across the model lifecycle.',
                    image: cdnUrl('home-page-updated-assets/nist-ai-rmfcompliance.svg')
                },
                {
                    name: 'ISO/IEC 23894',
                    description: 'AI-specific risk management guidance applied to deployed systems.',
                    image: cdnUrl('home-page-updated-assets/iso-iec-23894-compliance.svg')
                }
            ]
        },
        {
            iconType: 'chain',
            layer: 'Blockchain and Digital Asset Integrity',
            standards: [
                {
                    name: 'FATF Guidelines',
                    description: 'AML and KYC expectations for virtual asset services.',
                    image: cdnUrl('home-page-updated-assets/fatf-compliance.svg')
                },
                {
                    name: 'Smart Contract Security',
                    description: 'Audited contracts, invariant tests, and upgrade controls.',
                    image: cdnUrl('home-page-updated-assets/smart-contract-security-compliance.svg')
                },
                {
                    name: 'Travel Rule Considerations',
                    description: 'Originator and beneficiary data handled across transfers.',
                    image: cdnUrl('home-page-updated-assets/travel-rule-compliance.svg')
                },
                {
                    name: 'MiCA',
                    description: 'EU crypto-asset market rules for issuance and service providers.',
                    image: cdnUrl('home-page-updated-assets/mica-compliance.svg')
                },
                {
                    name: 'AML / CFT Controls',
                    description: 'Transaction monitoring and reporting against illicit finance.',
                    image: cdnUrl('home-page-updated-assets/aml-cft-controls-compliance.svg')
                },
                {
                    name: 'KYC Requirements',
                    description: 'Identity verification and risk scoring before onboarding.',
                    image: cdnUrl('home-page-updated-assets/kyc-requirements-compliance.svg')
                }
            ]
        },
        {
            iconType: 'chart',
            layer: 'Monitoring, Logging, & Incident Response',
            standards: [
                {
                    name: 'SIEM Practices',
                    description: 'Centralised, structured logs with alerting on anomalies.',
                    image: cdnUrl('home-page-updated-assets/siem-compliance.svg')
                },
                {
                    name: 'Incident Response Frameworks',
                    description: 'Defined severity levels, runbooks, and escalation paths.',
                    image: cdnUrl('home-page-updated-assets/incident-response-compliance.svg')
                },
                {
                    name: 'Audit Trails',
                    description: 'Tamper-evident records of access and privileged actions.',
                    image: cdnUrl('home-page-updated-assets/soc2-compliance.svg')
                },
                {
                    name: 'NIST 800-61',
                    description: 'Detection, containment, and recovery stages defined per incident.',
                    image: cdnUrl('home-page-updated-assets/nist-800-61-compliance.svg')
                },
                {
                    name: 'ISO/IEC 27035',
                    description: 'Structured incident management from reporting through lessons learned.',
                    image: cdnUrl('home-page-updated-assets/iso-iec-27035-compliance.svg')
                }
            ]
        },
        {
            iconType: 'database',
            layer: 'Business Continuity and Resilience',
            standards: [
                {
                    name: 'ISO 22301',
                    description: 'Continuity planning tested against defined recovery targets.',
                    image: cdnUrl('home-page-updated-assets/iso-22301-compliance.svg')
                },
                {
                    name: 'Disaster Recovery Standards',
                    description: 'RPO and RTO objectives agreed and rehearsed.',
                    image: cdnUrl('home-page-updated-assets/disaster-recovery-compliance.svg')
                },
                {
                    name: 'Backup Strategies',
                    description: 'Encrypted, versioned backups with restore verification.',
                    image: cdnUrl('home-page-updated-assets/backup-strategies-compliance.svg')
                },
                {
                    name: 'Business Impact Analysis',
                    description: 'Critical processes ranked by tolerable downtime and data loss.',
                    image: cdnUrl('home-page-updated-assets/business-impact-analysis-compliance.svg')
                },
                {
                    name: 'RTO / RPO Planning',
                    description: 'Recovery targets agreed per system and tested against them.',
                    image: cdnUrl('home-page-updated-assets/rto-rpo-planning-compliance.svg')
                }
            ]
        }
    ]
};

// ─── EngagementModels ─────────────────────────────────────────────────────────

// Priced like a plan card: `price` is the headline figure, `priceUnit` the small suffix
// that sits on its baseline, and `priceNote` the qualifying line beneath it.
export const EngagementModelsData = [
    {
        title: 'Fixed Price Engagement',
        price: 'From $15,000',
        priceUnit: '',
        priceNote: '1–3 months · agreed scope and milestones',
        description:
            'A scope-defined model with agreed requirements, milestones, cost, and delivery timeline established before development begins.',
        bestFor: [
            'Defined project scope',
            'Functional requirements',
            'Agreed UI/UX screens',
            'Scoped development work',
            'Testing and deployment support'
        ]
    },
    {
        title: 'Time & Materials Engagement',
        price: '$20 – $28',
        priceUnit: '/hr',
        priceNote: 'Flexible, sprint-based delivery',
        featured: true,
        description:
            'A flexible Agile model where engineering effort is billed against actual work completed while priorities can evolve across development sprints.',
        bestFor: [
            'Sprint backlog',
            'Prioritized development tasks',
            'Working feature increments',
            'QA within each sprint',
            'Sprint progress updates'
        ]
    },
    {
        title: 'Dedicated Development Team',
        price: 'From $3,200',
        priceUnit: '/month',
        priceNote: '160 hours per resource',
        description:
            'Dedicated engineers work as an extension of your team with predictable monthly capacity and direct alignment with your development roadmap.',
        bestFor: [
            'Assigned development resources',
            'Sprint-based engineering work',
            'Code commits and feature updates',
            'Bug fixes and technical support',
            'Regular work progress updates'
        ]
    }
];

export const EngagementModelsTrustBadges = ['7-Day Risk-Free Trial', 'No Minimum Engagement Duration', 'Part-Time Allocation Available'];

// ─── HomeDeliveryProcess ──────────────────────────────────────────────────────

export const HomeDeliveryProcessData = [
    {
        stepNumber: 1,
        category: 'Discovery & Architecture',
        heading: 'We Map Your Business Goals Into a Buildable Architecture',
        description:
            'We begin by mapping your business goals, user workflows, constraints, and technical requirements into a well-defined, buildable architecture. This includes system design, technology selection, integration planning, and scalability considerations — ensuring clarity before development begins and minimizing rework later.',
        deliverables: ['System Design Document', 'Technology Selection Report', 'Integration Map', 'Scalability Plan', 'Risk Assessment'],
        duration: '1–2 Weeks'
    },
    {
        stepNumber: 2,
        category: 'AI Model Selection',
        heading: 'The Right AI Model for Your Use Case, Not the Trendiest One',
        description:
            'Where AI is required, we evaluate and select the most suitable models — such as GPT-4o, Claude, Llama, or Gemini — based on your specific use case, data sensitivity, performance requirements, and compliance needs. The focus is on choosing the right model for your business, rather than forcing a particular technology.',
        deliverables: ['Model Evaluation Report', 'Compliance Fit Assessment', 'Cost-Performance Matrix', 'Data Pipeline Design'],
        duration: '1–2 Weeks'
    },
    {
        stepNumber: 3,
        category: 'Build & Integrate',
        heading: 'Structured Sprints. Continuous QA. Full Transparency.',
        description:
            'Our engineering teams develop in structured sprints with defined milestones, continuous QA, and transparent reporting. Core systems, APIs, and integrations are built alongside AI capabilities where relevant, ensuring everything works cohesively rather than as disconnected components.',
        deliverables: ['Sprint Demo (every 2 weeks)', 'Tested Milestones', 'QA Reports', 'Code Repository Access', 'Progress Dashboard'],
        duration: '4–16 Weeks'
    },
    {
        stepNumber: 4,
        category: 'Test & Deploy',
        heading: 'Rigorous Validation Before Every Production Release',
        description:
            'Each release goes through rigorous testing, including functional validation, performance checks, security assessments, and deployment readiness reviews. We follow structured deployment practices aligned with modern security and compliance standards to ensure stable and secure production environments.',
        deliverables: ['Security Audit Report', 'Load Test Results', 'Penetration Test Summary', 'Deployment Runbook'],
        duration: '1–2 Weeks'
    },
    {
        stepNumber: 5,
        category: 'Scale & Optimize',
        heading: 'Post-Launch Support That Improves Over Time',
        description:
            'Post-launch, we support continuous improvement through performance monitoring, infrastructure scaling, feature enhancements, and system optimization. Where AI is involved, this includes ongoing refinement to ensure outputs remain accurate, efficient, and aligned with evolving business needs.',
        deliverables: ['Performance Monitoring', 'Monthly Optimization Report', 'AI Model Retraining', 'SLA-Backed Support'],
        duration: 'Ongoing'
    }
];

// ─── LeadershipInsights ───────────────────────────────────────────────────────

const gurpreetImg = cdnImage('about-us-new/Gurpreet-Singh.webp');
const harryImg = cdnImage('about-us-new/Harry-Dhillon.webp');
const daljitImg = cdnImage('about-us-new/Daljit-Singh.webp');

export const LeadershipInsightsData = [
    {
        image: gurpreetImg,
        name: 'Gurpreet Singh',
        designation: 'Chief Executive Officer',
        quote: 'Most companies try to integrate AI after the architecture is already set. That is the wrong order. AI capabilities need to be designed into the data layer from day one. Retrofitting can cost three times more and deliver 40% less operational impact. We start every engagement by asking where AI needs to live in the architecture, not what AI feature can be added at the end.',
        linkedinUrl: 'https://www.linkedin.com/in/gurpreet-singh-630b5a12'
    },
    {
        image: daljitImg,
        name: 'Daljit Singh',
        designation: 'Co-founder & Director of AI & Emerging Technologies',
        quote: 'We have stepped into enough mid-crisis blockchain engagements to know exactly where things break down. Scope is often poorly defined. Architecture decisions are rushed under deadline pressure. Integration dependencies are overlooked until they become blockers. The process we follow today exists because of those lessons, and it helps our clients reach production without the chaos that derails many enterprise blockchain programs.',
        linkedinUrl: 'https://www.linkedin.com/in/daljit-singh-panesar-522b3918'
    },
    {
        image: harryImg,
        name: 'Harry Dhillon',
        designation: 'Director, Digital Transformation & Customer Success',
        quote: 'The real test of any technology initiative is not how impressive the prototype looks, but how well it fits the way a business actually operates. I believe AI and software should be designed around real workflows, connected systems, and measurable outcomes, with teams focused on moving from experimentation to dependable, everyday use.',
        linkedinUrl: 'https://www.linkedin.com/in/dhillonharry'
    }
];

const establishedEngineeringExpertise = cdnImage('home-page-new/Established-Engineering-Expertise.webp');
const ibm = cdnUrl('home-page-updated-assets/ibm-blockchain-engagement.webp');
const fabdep = cdnUrl('home-page-updated-assets/fabdep.webp');
const prodProvenAISystems = cdnImage('ai-development-new/Production-Proven-AI-Systems.webp');
const topRatedAIPartner = cdnImage('ai-development-new/Recognised-AI-Development-Partner.webp');

// Numbered proof points for the "Why Choose Debut Infotech" section. `image` is
// optional — a row without one renders as a two-column layout.
export const homeWhyChooseData = [
    {
        title: 'Recognised AI Development Partner',
        description:
            "Recognized among leading AI development companies on <a href='https://clutch.co/profile/debut-infotech' target='_blank' rel='noopener noreferrer'>Clutch</a> with strong client ratings and verified feedback. Our AI capabilities are also featured on GoodFirms and SelectedFirms, reflecting consistent delivery quality across AI and emerging technology solutions.",
        image: topRatedAIPartner,
        imageAlt: 'Top-rated badges from Clutch, GoodFirms and SelectedFirms'
    },
    {
        title: 'Established Engineering Expertise',
        description:
            'Active in software and technology delivery since 2011, with experience building and scaling enterprise systems across multiple technology cycles. This foundation ensures our AI implementations follow structured engineering practices, not experimental approaches.',
        image: establishedEngineeringExpertise,
        imageAlt: 'Engineering team with 2011 marking the year Debut Infotech started delivery'
    },
    {
        title: 'Production-Proven AI Systems',
        description:
            "Delivered AI systems across recruitment, real estate, and IT operations, including platforms such as <a href='/case-study/talentquest' target='_blank'>TalentQuest</a>, <a href='/case-study/titletech' target='_blank'>TitleTech</a>, and <a href='/case-study/aithentic' target='_blank'>Aithentic</a>. These systems operate in real environments, supporting decision-making, automation, and operational efficiency.",
        image: prodProvenAISystems,
        imageAlt: 'Production-proven AI systems delivered across recruitment, real estate, and IT operations'
    },
    {
        title: 'IBM & Enterprise Blockchain Engagement',
        description:
            'Hands-on experience with IBM blockchain initiatives, including enterprise solution design and implementation using Hyperledger Fabric and Corda frameworks. Familiar with permissioned blockchain networks, identity management, secure data exchange, and distributed enterprise system architecture.',
        image: ibm,
        imageAlt: 'Debut Infotech team at an IBM blockchain engagement'
    },
    {
        title: 'Creators of FabDep (Fabric Deployer)',
        description:
            'Developed FabDep, an internal Hyperledger Fabric deployment framework that simplifies enterprise blockchain network setup and deployment. In 2020, FabDep sponsored the Hyperledger Global Forum, showcasing our product-led blockchain expertise and growing recognition within the Hyperledger and enterprise blockchain community.',
        image: fabdep,
        imageAlt: 'FabDep — Debut Infotech’s Hyperledger Fabric deployment tool'
    }
];
